import { useLocale } from '../../providers/LocaleProvider';
import Styles from './LanguageSelector.module.css';
import { Button, Spinner } from '@app/engine';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** Props for the LanguageSelector. */
interface ILanguageSelectorProps {
  classNameTrigger?: string;
}

/** File-local constants for the language selector. */
const localConstants = {
  pointerEvent: 'pointerdown'
} as const;

/**
 * Compact language selector shared by the landing header and the dashboard topbar. Renders the active language as a text trigger that opens a dropdown of the supported languages; choosing one
 * switches the active locale (which re-translates the localization dictionary). Closes on outside click.
 *
 * @param props - Component props.
 * @returns The language selector element.
 */
export function LanguageSelector(props: ILanguageSelectorProps) {
  const { languages, selectedLang, selectLang, langLoaded } = useLocale();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const activeLabel = useMemo(() => languages.find((lang) => lang.code === selectedLang)?.label ?? selectedLang, [languages, selectedLang]);

  // Close the menu when clicking outside it.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener(localConstants.pointerEvent, onPointerDown);
    return () => document.removeEventListener(localConstants.pointerEvent, onPointerDown);
  }, [open]);

  const choose = useCallback(
    (code: string) => {
      selectLang(code);
      setOpen(false);
    },
    [selectLang]
  );

  // While a language switch is in flight the dictionary is mid-translation, so show a spinner in place of the
  // trigger rather than the stale active label.
  if (!langLoaded) {
    return (
      <div ref={rootRef} className={Styles.langSelector}>
        <Spinner classNameContainer={Styles.langSpinner} classNameRing={Styles.langSpinnerRing} />
      </div>
    );
  }

  const triggerClass = props.classNameTrigger ? props.classNameTrigger : Styles.langTrigger;

  return (
    <div ref={rootRef} className={Styles.langSelector}>
      <Button text={activeLabel} type="button" classNameButtonEnabled={triggerClass} onClickHandler={() => setOpen((prev) => !prev)} />
      {open && (
        <div className={Styles.langMenu} role="listbox">
          {languages.map((lang) => (
            <Button
              key={lang.code}
              text={lang.label}
              type="button"
              classNameButtonEnabled={lang.code === selectedLang ? `${Styles.langOption} ${Styles.langOptionActive}` : Styles.langOption}
              onClickHandler={() => choose(lang.code)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
